import { useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Drawer } from 'src/components/shared/Drawer';
import { OfflineDriveCandidate, OfflineDriveCandidateStatus, OfflineDriveCandidateStatusHistoryData } from 'types';
import OfflineDriveRoundStatusHistory from './OfflineDriveRoundStatusHistory';
import dayjs from 'dayjs';

interface Props {
	candidate: OfflineDriveCandidate;
}

export function statusIcon(status: OfflineDriveCandidateStatus) {
	let bgColor;
	switch (status) {
		case OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED:
			bgColor = 'bg-yellow-400';
			break;
		case OfflineDriveCandidateStatus.BUSY:
			bgColor = 'bg-red-500';
			break;
		case OfflineDriveCandidateStatus.ASSIGN:
			bgColor = 'bg-blue-500';
			break;
		case OfflineDriveCandidateStatus.HIRED:
			bgColor = 'bg-green-800';
			break;
		case OfflineDriveCandidateStatus.IDLE:
			bgColor = 'bg-[#39D98A]';
			break;
		case OfflineDriveCandidateStatus.OUT_OF_DRIVE:
			bgColor = 'bg-slate-500';
			break;
		default:
			bgColor = 'bg-[#39D98A]';
			break;
	}

	return <div className={`h-3 w-3 min-w-3 rounded-full ${bgColor}`}></div>;
}

export function getTimer(
	status: OfflineDriveCandidateStatus,
	offlineDriveCandidateStatusHistoryData: OfflineDriveCandidateStatusHistoryData[],
) {
	if (status === OfflineDriveCandidateStatus.BUSY || status === OfflineDriveCandidateStatus.ASSIGN) {
		const attendanceNotMarked: OfflineDriveCandidateStatusHistoryData = offlineDriveCandidateStatusHistoryData?.sort(
			(a, b) => dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix(),
		)[0];

		const time = new Date(attendanceNotMarked?.updatedAt);
		const currentTime = new Date();
		const diff = currentTime.getTime() - time.getTime();
		const hours = Math.floor(diff / 3600000);
		const minutes = Math.floor((diff % 3600000) / 60000);
		const seconds = Math.floor((diff % 60000) / 1000);
		return `${hours ? hours.toString().padStart(2, '0') + 'h' : ''} ${minutes.toString().padStart(2, '0')}m ${
			hours ? '' : seconds.toString().padStart(2, '0') + 's'
		}`;
	}
	return '';
}

const CandidateCurrentStatus = ({ candidate }: Props) => {
	const [timer, setTimer] = useState('');
	const isMobile = useMediaQuery('(max-width: 960px)');
	const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);

	useEffect(() => {
		if (candidate && !candidate?.isPlaced) {
			const interval = setInterval(() => {
				const updatedTimer = getTimer(
					candidate?.status as OfflineDriveCandidateStatus,
					candidate?.offlineDriveCandidateStatusHistory,
				);
				setTimer(updatedTimer);
			}, 1000);

			return () => clearInterval(interval);
		}
	}, [candidate]);

	return (
		<>
			<div className="flex flex-col gap-1 font-inter">
				<div className="flex justify-start gap-1">
					<div
						className="flex gap-2 items-center justify-start hover:underline cursor-pointer"
						onClick={() => {
							setShowStatusHistoryModal(true);
						}}
					>
						{statusIcon(candidate?.status as OfflineDriveCandidateStatus)}
						<p className="text-[#28293D] text-base font-medium capitalize">
							{// move underscore and capitalize first letter
							candidate?.status
								.replace(/_/g, ' ')
								.split(' ')
								.map(word => word.charAt(0).toUpperCase() + word.slice(1))
								.join(' ')}
						</p>
					</div>
					{candidate?.status === OfflineDriveCandidateStatus.BUSY ||
					candidate?.status === OfflineDriveCandidateStatus.ASSIGN ? (
						<div className="flex items-center gap-[3.2px] p-[1.6px_3.2px] rounded-[3.2px] border-[0.5px] border-[#FDAC42] bg-[#FFF8E5] min-w-fit">
							<img src="/assets/ClockCountdown.svg" alt="timer" className="w-3 h-3" />
							<p className="text-[#28293D] text-center font-inter text-[9.6px] font-normal whitespace-pre">{timer}</p>
						</div>
					) : null}
				</div>
				<div className="flex items-center">
					<div className="flex flex-col flex-1 text-center pr-1">
						<p className="text-[#555770] text-xs">Test</p>
						<p className="text-[#555770] text-sm font-medium">{candidate?.totalAssignmentRounds}</p>
					</div>
					<div className="flex flex-col flex-1 text-center border-x-[1px] px-1">
						<p className="text-[#555770] text-xs">Interview</p>
						<p className="text-[#555770] text-sm font-medium">{candidate?.totalInterviewRounds}</p>
					</div>
					<div className="flex flex-col flex-1 text-center pl-1">
						<p className="text-[#555770] text-xs">Total</p>
						<p className="text-[#555770] text-sm font-medium">{candidate?.totalRounds}</p>
					</div>
				</div>
			</div>

			<Drawer
				fullWidth={true}
				isModal={!isMobile}
				isOpen={showStatusHistoryModal}
				onClose={() => setShowStatusHistoryModal(false)}
				header={
					<div className="p-3 pb-0">
						<strong className="text-lg capitalize">{candidate?.name?.toLowerCase()}'s</strong>{' '}
						<span className="text-lg font-[400] text-[#1F3343]">status history</span>
					</div>
				}
				body={
					<div className="p-3">
						<OfflineDriveRoundStatusHistory
							offlineDriveCandidateStatusHistory={candidate?.offlineDriveCandidateStatusHistory}
						/>
					</div>
				}
			/>
		</>
	);
};

export default CandidateCurrentStatus;
